import React, { useMemo } from 'react'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'

import get from 'lodash/get'

import DropdownIndicator from './DropdownIndicator'
import wrapSelect from './wrapSelect'
import cn from 'classnames'

import styles from './Select.module.scss'
import SelectVirtualList from './components/SelectVirtualList'
import { ReactComponent as RemoveOptionIcon } from 'media/icons/delete.svg'

const Field = ({ style, ...props }) => {
	const {
		noForm,
		defaultValue,
		fieldState,
		beforeSetSelectValue,
		beforeSetFormValue,
		components,
		valueField = 'value',
		labelField = 'label',
		className,
		disabled,
		withVirtualBody,
		resolvePlaceholder,
		simpleValue = true,
		smartSubmit,
		field,
		fieldApi,
		onChange,
		removeOnClear,
		value,
		isCreatable = false,
		uncontrolled,
		readOnly,
		...setSelectProps
	} = props

	const { options = [], isSearchable = false, isMulti, closeMenuOnSelect } = setSelectProps
	const selectOptions = withVirtualBody && !!props?.optionsEditor ? props?.optionsEditor(options) : options

	const getLabel = (option) => get(option, labelField) || get(option, 'label')
	const getValue = (option) => get(option, valueField) || get(option, 'value')

	const handleChange = async val => {
		const option = isMulti && val === null ? [] : val // workaround https://github.com/JedWatson/react-select/issues/3632
		let newValue = !!option && simpleValue && !Array.isArray(option) ? getValue(option) : option
		newValue = beforeSetFormValue ? await beforeSetFormValue(newValue, getLabel(option)) : newValue
		if(onChange) {
			onChange(option)
		}
		if(!noForm) {
			if(!option && !newValue) {
				if(removeOnClear) {
					fieldApi.setValue()
				} else {
					fieldApi.setValue(null)
				}
			} else {
				fieldApi.setValue(newValue)
			}
			if(smartSubmit) {
				smartSubmit.submit()
			}
		}
	}
	const simpleValueToOption = val => {
		if(typeof val === 'undefined') {
			return null
		}
		// Not strict equality for possibility compare numbers with strings like 1 == '1'
		return selectOptions.find(option => getValue(option) == val) || null // eslint-disable-line eqeqeq
	}
	const getValueProp = val => {
		if(typeof val === 'undefined') {
			return val
		}
		let out = val
		if(simpleValue && isMulti) {
			out = val.map(simpleValueToOption)
		}
		if(simpleValue && !isMulti) {
			out = simpleValueToOption(val)
		}
		return beforeSetSelectValue ? beforeSetSelectValue(out) : out
	}
	const selectComponents = useMemo(() => ({
		...components,
		DropdownIndicator,
		MultiValueRemove: ({ innerProps }) => <RemoveOptionIcon {...innerProps} className={styles.multiValueRemove}/>,
		...withVirtualBody && { MenuList: SelectVirtualList }
	}), [components, withVirtualBody])
	let selectProps = {
		getOptionLabel: (option) => option[labelField] || option.label,
		getOptionValue: (option) => option[valueField] || option.value,
		placeholder: resolvePlaceholder('select'),
		...setSelectProps,
		options: selectOptions,
		getOptions: (data) => data,
		onChange: handleChange,
		isSearchable: isSearchable || isCreatable,
		className: cn(className, { [styles['with-search']]: isSearchable }),
		classNamePrefix: 'Select',
		blurInputOnSelect: isMulti || closeMenuOnSelect === false ? false : isSearchable, // Removes search icon on select
		components: selectComponents,
		...disabled && { isDisabled: true },
		menuPlacement: 'auto',
		isDisabled: readOnly
	}

	if(simpleValue) {
		selectProps.defaultValue = getValueProp(noForm ? (value || defaultValue) : fieldState.value)
		if(!uncontrolled) {
			selectProps.value = getValueProp(noForm ? (value || defaultValue) : fieldState.value)
		}
	} else {
		selectProps.defaultValue = getValueProp(defaultValue)
		if(!uncontrolled) {
			selectProps.value = getValueProp(noForm ? value : fieldState.value)
		}
	}

	return isCreatable ? <CreatableSelect {...selectProps}/> : <Select {...selectProps}/>
}

export default wrapSelect(Field)
